import { Link } from '@inertiajs/react'
import { MessageSquare, User, AlertCircle, Plus } from 'lucide-react'
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import WhatsAppBadge from '@/components/WhatsAppBadge'
import type { Team, AssignmentFilterType, ChannelFilterType } from './types'

interface FilterSidebarProps {
  filter: string
  statusFilter: string
  teamFilter: string | null
  channelFilter: string | null
  stats: {
    all: number
    assigned_to_me: number
    unassigned: number
  }
  teams: Team[]
  selectedConversationId: number | null
  buildFilterUrl: (
    conversationId?: number | null,
    assignmentFilter?: string,
    status?: string,
    teamId?: string | null,
    channel?: string | null
  ) => string
  onStartNewConversation: () => void
}

const ASSIGNMENT_FILTERS: Array<{
  key: AssignmentFilterType
  label: string
  icon: React.ReactNode
  statsKey: 'assigned_to_me' | 'unassigned' | 'all'
}> = [
  { key: 'assigned_to_me', label: 'Minhas', icon: <User className="w-4 h-4" />, statsKey: 'assigned_to_me' },
  { key: 'unassigned', label: 'Não atribuídas', icon: <AlertCircle className="w-4 h-4" />, statsKey: 'unassigned' },
  { key: 'all', label: 'Todas', icon: <MessageSquare className="w-4 h-4" />, statsKey: 'all' },
]

const CHANNEL_FILTERS: Array<{
  key: ChannelFilterType
  label: string
  icon: React.ReactNode
}> = [
  { key: null, label: 'Todas', icon: <MessageSquare className="w-4 h-4" /> },
  { key: 'whatsapp', label: 'WhatsApp', icon: <WhatsAppBadge size="sm" className="text-green-600" /> },
  { key: 'chat', label: 'Chat', icon: <MessageSquare className="w-4 h-4" /> },
]

export default function FilterSidebar({
  filter,
  statusFilter,
  teamFilter,
  channelFilter,
  stats,
  teams,
  selectedConversationId,
  buildFilterUrl,
  onStartNewConversation,
}: FilterSidebarProps) {
  return (
    <aside className="w-[238px] shrink-0 bg-[#fdfdfd] border-r border-gray-200 flex flex-col">
      <div className="h-14 flex items-center justify-between px-4 border-b border-gray-200">
        <span className="text-lg font-bold text-gray-900">Caixa de Entrada</span>
        <Tooltip>
          <TooltipTrigger asChild>
            <button
              type="button"
              onClick={onStartNewConversation}
              className="p-1.5 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors cursor-pointer"
            >
              <Plus className="w-4 h-4" />
            </button>
          </TooltipTrigger>
          <TooltipContent side="bottom">Nova conversa</TooltipContent>
        </Tooltip>
      </div>

      <nav className="flex-1 py-3 px-2 space-y-0.5 overflow-y-auto">
        {ASSIGNMENT_FILTERS.map((item) => {
          const count = stats[item.statsKey]
          const isActive = filter === item.key && !teamFilter
          return (
            <Link
              key={item.key}
              href={buildFilterUrl(selectedConversationId, item.key, statusFilter)}
              preserveState
              className={`flex items-center justify-between px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                isActive
                  ? 'bg-blue-50 text-blue-700'
                  : 'text-gray-700 hover:bg-gray-100'
              }`}
            >
              <div className="flex items-center gap-2">
                <span className={isActive ? 'text-blue-600' : 'text-gray-400'}>
                  {item.icon}
                </span>
                {item.label}
              </div>
              {count > 0 && (
                <span
                  className={`px-1.5 py-0.5 text-xs rounded-full ${
                    isActive
                      ? 'bg-blue-100 text-blue-700'
                      : 'bg-gray-100 text-gray-600'
                  }`}
                >
                  {count}
                </span>
              )}
            </Link>
          )
        })}

        {/* Channel filters section */}
        <div className="mt-4 mb-1 px-3 text-[11px] font-semibold text-gray-400 uppercase tracking-wider">
          Canais
        </div>
        {CHANNEL_FILTERS.map((item) => {
          const isActive = item.key === null ? !channelFilter : channelFilter === item.key
          return (
            <Link
              key={item.key ?? 'all'}
              href={buildFilterUrl(selectedConversationId, filter, statusFilter, teamFilter, item.key)}
              preserveState
              className={`flex items-center justify-between px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                isActive
                  ? 'bg-blue-50 text-blue-700'
                  : 'text-gray-700 hover:bg-gray-100'
              }`}
            >
              <div className="flex items-center gap-2">
                <span className={isActive ? 'text-blue-600' : 'text-gray-400'}>
                  {item.icon}
                </span>
                {item.label}
              </div>
            </Link>
          )
        })}

        {/* Teams section */}
        {teams.length > 0 && (
          <>
            <div className="mt-4 mb-1 px-3 text-[11px] font-semibold text-gray-400 uppercase tracking-wider">
              Equipes
            </div>
            {teams.map((team) => {
              const isActive = teamFilter === String(team.id)
              return (
                <Link
                  key={team.id}
                  href={buildFilterUrl(null, filter, statusFilter, isActive ? null : String(team.id))}
                  preserveState
                  className={`flex items-center justify-between px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                    isActive
                      ? 'bg-blue-50 text-blue-700'
                      : 'text-gray-700 hover:bg-gray-100'
                  }`}
                >
                  <div className="flex items-center gap-2">
                    <span className="text-sm">{team.emoji || '👥'}</span>
                    {team.name}
                  </div>
                  {team.open_count > 0 && (
                    <span
                      className={`px-1.5 py-0.5 text-xs rounded-full ${
                        isActive
                          ? 'bg-blue-100 text-blue-700'
                          : 'bg-gray-100 text-gray-600'
                      }`}
                    >
                      {team.open_count}
                    </span>
                  )}
                </Link>
              )
            })}
          </>
        )}
      </nav>
    </aside>
  )
}
